/**
 * calcCSMT — Casement window cut list calculations
 *
 * All formulas verified line-by-line against CSMT DNA spreadsheet.
 * Excel cell references noted inline for auditing.
 *
 * WindowRow.jsx stores glass fields with prefix "Casement":
 *   win.CasementGlassPkg   win.CasementGlassOpts
 *   win.CasementGridType   win.CasementGridPat   win.CasementGridV   win.CasementGridH
 *
 * Formula key (DNA sheet):
 *   B12  = overallW (overall width input)
 *   C12  = height   (overall height input)
 *   B48  = indivW   = overallW / numLites  (Mfg Size W)
 *   C48  = indivH   = height               (Mfg Size H)
 *   C49  = sashWRef = indivW-1.625 (White) | indivW-2.375 (Woodgrain)
 *   D49  = sashHRef = indivH-1.625 (White) | indivH-2.375 (Woodgrain)
 *   C50  = centerSashW_ref = overallW/2-6   (Q/H/Q only)
 *   D50  = centerSashH_ref = height-3       (Q/H/Q only)
 *   D12  = qty (raw window qty — NOT totalQty)
 *
 * FIXES vs previous version:
 *  1.  Frame qty:            was totalQty*2,  now qty*2     (DNA: D12*2)
 *  2.  Sash Widths qty:      q/h/q was qty*4, now qty*6    (DNA: q/h/q D12*6)
 *  3.  Glazbead Heights qty: q/h/q was qty*4, now qty*6    (DNA: q/h/q D12*6)
 *  4.  Glazbead Widths qty:  q/h/q was qty*4, now qty*6    (DNA: q/h/q D12*6)
 *  5.  Center glass W:       was glassW,  now C50-3.625=overallW/2-9.625
 *  6.  Center glass H:       was glassH,  now D50-3.625=height-6.625
 *  7.  Center sash W cut:    was overallW/2-6, now C50-1=overallW/2-7
 *  8.  5464 Glazbead C W:    was centerSashW-2.125, now C50-4.5=overallW/2-10.5
 *  9.  Part numbers:         no color suffix on frame/sash/mullpost/jchannel parts (DNA shows bare numbers)
 *  10. Console.log removed
 *  11. Field names:          now reads CasementGlassPkg/Opts/GridType/Pat/V/H
 *  12. Grid fields:          fully wired with hasGrids, gridLenH/V, horizQty, vertQty
 *  13. Sash Weld:            moved out of misc[], lives in summary only
 */
export function calcCSMT(win) {
  const {
    // ── Dimensions ──────────────────────────────────────────────────────────
    width: overallW,
    height,
    // ── Identity ────────────────────────────────────────────────────────────
    config  = 'Single',
    color   = 'White',
    finType = 'Without Fin',
    qty     = 1,
    label   = '',
    handing = '',
    mullType  = '',
    mullCount = 0,
    // ── Screen ──────────────────────────────────────────────────────────────
    screenType = 'Flex Scrn',
    screenSize = 'Full Scrn',
    meshType   = 'STD Mesh',
    // ── Gas ─────────────────────────────────────────────────────────────────
    gasFill = 'Argon',
    // ── Glass — CSMT uses Casement prefix ───────────────────────────────────
    CasementGlassPkg  = 'DLA',
    CasementGlassOpts = 'None',
    CasementGridType  = 'None',
    CasementGridPat   = 'None',
    CasementGridV     = '0',
    CasementGridH     = '0',
  } = win;

  // ── Color / fin helpers ───────────────────────────────────────────────────
  const colorLower    = (color || '').toLowerCase();
  const isWoodgrain   = colorLower.includes('woodgrain');
  const withFin       = finType?.toLowerCase().includes('with fin');
  const withFinAndJ   = finType?.toLowerCase().includes('with fin & j')
                     || finType?.toLowerCase().includes('with fin&j');

  // ── Lite multiplier ───────────────────────────────────────────────────────
  // DNA: B48 = IF(Mulled, overallW/numLites, overallW)
  // For CSMT "Mulled" = when config is NOT Single
  const liteMap = {
    Single:   1,
    '2-Lite': 2,
    '3-Lite': 3,
    '4-Lite': 4,
    '5-Lite': 5,
    'Q/H/Q':  2,
  };
  const numLites  = liteMap[config] || 1;
  const isMulled  = numLites > 1;
  const isQHQ     = config === 'Q/H/Q';
  const indivW    = isMulled ? overallW / numLites : overallW; // B48
  const indivH    = height;                                    // C48
  const totalQty  = qty * numLites;                            // D12 × numLites

  // ── Config-based qty helper ───────────────────────────────────────────────
  // DNA uses D12 (= raw qty) multiplied by a per-config factor.
  // Returns qty × factor matching DNA IF chain.
  // For Q/H/Q the factor differs by section — pass explicitly.
  const qtyFor = factor => qty * factor;
  const configQty = (s1, s2, s3, s4, s5, sqhq) => {
    const map = { Single:s1,'2-Lite':s2,'3-Lite':s3,'4-Lite':s4,'5-Lite':s5,'Q/H/Q':sqhq };
    return qty * (map[config] ?? s1);
  };

  // ── Sash reference row (C49/D49) ──────────────────────────────────────────
  // C49 = IF(woodgrain, indivW-2.375, indivW-1.625)
  // D49 = IF(woodgrain, indivH-2.375, indivH-1.625)
  const sashWRef = isWoodgrain ? (indivW - 2.375) : (indivW - 1.625);  // C49
  const sashHRef = isWoodgrain ? (indivH - 2.375) : (indivH - 1.625);  // D49

  // ── Center pane reference (Q/H/Q only — C50/D50) ──────────────────────────
  // C Sash Size: W=B12/2-6=overallW/2-6,  H=C12-3=height-3
  // C50 = centerSashW_ref = overallW/2 - 6
  // D50 = centerSashH_ref = height - 3
  const centerSashW_ref = overallW / 2 - 6;   // C50
  const centerSashH_ref = height - 3;          // D50

  // ── Glass ─────────────────────────────────────────────────────────────────
  // Main glass: W=C49-2.375=sashWRef-2.375, H=D49-2.375=sashHRef-2.375
  // Main qty: single=D12*1, 2-lite=D12*2, ..., q/h/q=D12*2
  // C Glass (Q/H/Q): W=C50-3.625=overallW/2-9.625, H=D50-3.625=height-6.625
  // C Glass qty: IF(q/h/q, D12*1, "0")
  const glassW       = sashWRef - 2.375;             // C49-2.375
  const glassH       = sashHRef - 2.375;             // D49-2.375
  const centerGlassW = centerSashW_ref - 3.625;      // C50-3.625 = overallW/2-9.625
  const centerGlassH = centerSashH_ref - 3.625;      // D50-3.625 = height-6.625

  // ── Grid helper ───────────────────────────────────────────────────────────
  const gridV    = parseInt(CasementGridV) || 0;
  const gridH    = parseInt(CasementGridH) || 0;
  const hasGrids = !!CasementGridType
                && CasementGridType !== 'None'
                && CasementGridType !== ''
                && (gridV > 0 || gridH > 0);

  const gridLenH  = hasGrids ? glassW - 1 : null;
  const gridLenV  = hasGrids ? glassH - 1 : null;
  const horizQty  = hasGrids ? gridH * totalQty : 0;   // DNA: =M17*L9
  const vertQty   = hasGrids ? gridV * totalQty : 0;   // DNA: =L17*L9
  const gridColor = hasGrids ? color : '';

  // ── Center grid (Q/H/Q — same glass type as main) ─────────────────────────
  const cGridLenH = hasGrids ? centerGlassW - 1 : null;
  const cGridLenV = hasGrids ? centerGlassH - 1 : null;
  const cHorizQty = hasGrids ? gridH * qty : 0;
  const cVertQty  = hasGrids ? gridV * qty : 0;

  // ── Notch ─────────────────────────────────────────────────────────────────
  // DNA: IF(B20<=31.9375,"4''Notch","7''Notch") where B20=indivW
  const notch = indivW <= 31.9375 ? "4'' Notch" : "7'' Notch";

  // ── Center of Mull ────────────────────────────────────────────────────────
  // DNA: IF(single,0, 2-lite (overallW-5.25)/2, 3-lite (overallW-5.25)/3...)
  const mullCenter = numLites > 1 ? (overallW - 5.25) / numLites : 0;

  // ── Frame ─────────────────────────────────────────────────────────────────
  // 8065 Frame Width:  qty=D12*2=qty*2, length=B48+0.25=indivW+0.25
  // 8065 Frame Height: qty=D12*2=qty*2, length=C48+0.25=indivH+0.25
  // Parts: 5152/5452 (with/without fin) — NO color suffix per DNA
  const frameW     = indivW + 0.25;
  const frameH     = indivH + 0.25;
  const frameWPart = withFin ? '5152' : '5452';
  const frameHPart = withFin ? '5150' : '5450';

  // ── Mull Posts ────────────────────────────────────────────────────────────
  // qty: single=0, 2-lite=D12*1, 3-lite=D12*2, ..., q/h/q=D12*2
  // length=B48+1.5=indivW+1.5
  // Part: 5191 (with fin) / 5190 (without fin) — no color suffix per DNA
  const mullPostQty  = configQty(0, 1, 2, 3, 4, 2);   // (numLites-1)*qty ≡ this
  const mullPostLen  = indivW + 1.5;
  const mullPostPart = withFin ? '5191' : '5190';

  // ── Sash ──────────────────────────────────────────────────────────────────
  // 5432 Sash Widths:
  //   qty: single=D12*2, 2-lite=D12*4, 3-lite=D12*6, 4-lite=D12*8, 5-lite=D12*10, q/h/q=D12*6
  //   length=C49+0.25=sashWRef+0.25
  //   part=5540 (no color suffix per DNA)
  //
  // 5432 Sash Heights:
  //   qty: single=D12*2, 2-lite=D12*4, 3-lite=D12*6, 4-lite=D12*8, 5-lite=D12*10, q/h/q=D12*4
  //   length=D49+0.25=sashHRef+0.25
  //   part=5543 (no color suffix per DNA)
  //
  // 5432 Center Sash Widths (Q/H/Q only):
  //   qty=D12*2=qty*2
  //   length=C50-1=centerSashW_ref-1=overallW/2-7
  //   part=5540
  const sashWCut  = sashWRef + 0.25;
  const sashHCut  = sashHRef + 0.25;
  const sashWQty  = configQty(2, 4, 6, 8, 10, 6);
  const sashHQty  = configQty(2, 4, 6, 8, 10, 4);
  const centerSashWCut = centerSashW_ref - 1;   // C50-1 = overallW/2-7

  // ── J-Channel ─────────────────────────────────────────────────────────────
  // qty=IF(WITH FIN & J, D12*2, 0) — raw qty * 2, NOT totalQty
  // J-Channel Widths length  = B48+1.5 = indivW+1.5
  // J-Channel Heights length = C48+1.5 = indivH+1.5
  // Parts: 5541 / 5542 per DNA (no color suffix)
  const jChanQty = withFinAndJ ? qty * 2 : 0;
  const jChanW   = indivW + 1.5;
  const jChanH   = indivH + 1.5;

  // ── Glazbead ─────────────────────────────────────────────────────────────
  // 5470 Glazbead Heights:
  //   qty: single=D12*2, 2-lite=D12*4, ..., q/h/q=D12*6
  //   length=D49-3.25=sashHRef-3.25
  //   part=5457 (no color suffix per DNA)
  //
  // 5470 Glazbead Widths:
  //   qty: single=D12*2, 2-lite=D12*4, ..., q/h/q=D12*6
  //   length=C49-2.125=sashWRef-2.125
  //   part=5457
  //
  // 5464 Glazbead C Widths (Q/H/Q only):
  //   qty=D12*2=qty*2
  //   length=C50-4.5=centerSashW_ref-4.5=overallW/2-10.5
  //   part=5457
  const glazHQty     = configQty(2, 4, 6, 8, 10, 6);
  const glazWQty     = configQty(2, 4, 6, 8, 10, 6);
  const glazbeadH    = sashHRef - 3.25;
  const glazbeadW    = sashWRef - 2.125;
  const centerGlazW  = centerSashW_ref - 4.5;   // C50-4.5 = overallW/2-10.5

  // ── Sash Weld Size ────────────────────────────────────────────────────────
  // DNA: H = IF(White, C48+0.75, C48) = IF(White, indivH+0.75, indivH)
  //      W = IF(White, B48+0.75, B48) = IF(White, indivW+0.75, indivW)
  const isWhite  = !isWoodgrain;
  const sashWeldH = isWhite ? (indivH + 0.75) : indivH;
  const sashWeldW = isWhite ? (indivW + 0.75) : indivW;

  // ── Starter ───────────────────────────────────────────────────────────────
  // DNA: qty=IF(without fin, D12*1, IF(with fin, D12*0)) — raw qty, not totalQty
  // length=B12=overallW
  const starterQty = withFin ? 0 : qty;

  // ── Screens ───────────────────────────────────────────────────────────────
  // Flex Screen: W=C48-4.375=indivH-4.375  (HEIGHT used for width — casement orientation)
  //              H=D48-4.1875=indivW-4.1875 (WIDTH used for height)
  //              qty=B49=indivW ref → actually qty=totalQty per pattern
  // Std Screen:  W=B20-7.3125=indivW-7.3125
  //              H=C20-7.3125=indivH-7.3125
  //              qty: single=D12*2, 2-lite=D12*4, ..., q/h/q=D12*4
  const isFlexScrn = screenType === 'Flex Scrn';
  const isStdScrn  = screenType === 'Std Scrn';

  const flexScreen = isFlexScrn ? {
    qty:       totalQty,
    width:     indivH - 4.375,    // C48-4.375  (uses HEIGHT)
    height:    indivW - 4.1875,   // D48-4.1875 (uses WIDTH)
    meshType:  meshType || 'Plain',
    screenType,
    screenSize,
    label,
  } : null;

  const stdScreenW   = indivW - 7.3125;
  const stdScreenH   = indivH - 7.3125;
  const stdScreenQty = isStdScrn
    ? configQty(2, 4, 6, 8, 10, 4)
    : 0;

  // ── Return ────────────────────────────────────────────────────────────────
  return {
    label,
    type:      'CSMT',
    color,
    overallW,
    width:     overallW,
    height,
    config,
    qty,
    totalQty,
    indivW,
    handing,
    notch,
    mullType,
    mullCenter,
    finType,
    screenType,
    screenSize,

    // ── Glass ─────────────────────────────────────────────────────────────────
    glass: [
      {
        label:       'Casement Glass',
        qty:         totalQty,                    // D12 × numLites
        width:       glassW,                      // C49-2.375
        height:      glassH,                      // D49-2.375
        glassType:   CasementGlassPkg,
        glassConfig: CasementGlassOpts !== 'None' ? CasementGlassOpts : '',
        gasFill,
        grids:       hasGrids,
        gridType:    hasGrids ? CasementGridType : '',
        gridPattern: hasGrids ? (CasementGridPat === 'None' ? '' : CasementGridPat) : '',
        gridQtyV:    hasGrids ? gridV : 0,
        gridQtyH:    hasGrids ? gridH : 0,
        gridColor:   hasGrids ? gridColor : '',
        gridLenH,
        gridLenV,
        horizQty,
        vertQty,
      },
      ...(isQHQ ? [{
        label:       'Center Glass',
        qty,                                      // D12*1
        width:       centerGlassW,                // C50-3.625 = overallW/2-9.625
        height:      centerGlassH,                // D50-3.625 = height-6.625
        glassType:   CasementGlassPkg,
        glassConfig: CasementGlassOpts !== 'None' ? CasementGlassOpts : '',
        gasFill,
        grids:       hasGrids,
        gridType:    hasGrids ? CasementGridType : '',
        gridPattern: hasGrids ? (CasementGridPat === 'None' ? '' : CasementGridPat) : '',
        gridQtyV:    hasGrids ? gridV : 0,
        gridQtyH:    hasGrids ? gridH : 0,
        gridColor:   hasGrids ? gridColor : '',
        gridLenH:    cGridLenH,
        gridLenV:    cGridLenV,
        horizQty:    cHorizQty,
        vertQty:     cVertQty,
      }] : []),
    ],

    // ── Frame ─────────────────────────────────────────────────────────────────
    // qty=D12*2=qty*2  (NOT totalQty*2)
    frame: [
      { label: '8065 Frame Widths',  qty: qty * 2, length: frameW, part: frameWPart },  // D12*2
      { label: '8065 Frame Heights', qty: qty * 2, length: frameH, part: frameHPart },  // D12*2
    ],

    // ── Mull Posts ────────────────────────────────────────────────────────────
    mullPosts: mullPostQty > 0 ? [
      { label: '5739 Mull Posts', qty: mullPostQty, length: mullPostLen, part: mullPostPart },
    ] : [],

    // ── Sash ──────────────────────────────────────────────────────────────────
    sash: [
      { label: '5432 Sash Widths',  qty: sashWQty, length: sashWCut,     part: '5540' }, // config-based qty
      { label: '5432 Sash Heights', qty: sashHQty, length: sashHCut,     part: '5543' }, // q/h/q=D12*4
      ...(isQHQ ? [
        { label: '5432 Ctr Sash Widths', qty: qty * 2, length: centerSashWCut, part: '5540' }, // D12*2, C50-1
      ] : []),
    ],

    // ── J-Channel ─────────────────────────────────────────────────────────────
    jChannel: jChanQty > 0 ? [
      { label: 'J-Channel Widths',  qty: jChanQty, length: jChanW, part: '5541' },
      { label: 'J-Channel Heights', qty: jChanQty, length: jChanH, part: '5542' },
    ] : [],

    // ── Glazbead ──────────────────────────────────────────────────────────────
    glazbead: [
      { label: '5470 Glazbead Heights', qty: glazHQty, length: glazbeadH, part: '5457' }, // q/h/q=D12*6
      { label: '5470 Glazbead Widths',  qty: glazWQty, length: glazbeadW, part: '5457' }, // q/h/q=D12*6
      ...(isQHQ ? [
        { label: '5464 Glazbead C Widths', qty: qty * 2, length: centerGlazW, part: '5457' }, // D12*2, C50-4.5
      ] : []),
    ],

    // ── Misc ──────────────────────────────────────────────────────────────────
    misc: [
      ...(starterQty > 0 ? [
        { label: 'Starter', qty: starterQty, length: overallW, part: `8351-${color}` }, // D12*1
      ] : []),
      ...(isStdScrn && stdScreenQty > 0 ? [
        { label: 'Screen Widths',  qty: stdScreenQty, length: stdScreenW },
        { label: 'Screen Heights', qty: stdScreenQty, length: stdScreenH },
      ] : []),
      ...(mullCount > 0 ? [
        { label: '(x2) Snap Mull', qty: mullCount, length: indivH - 0.125, part: `8309-${color}` },
      ] : []),
    ],

    // ── Screens ───────────────────────────────────────────────────────────────
    flexScreen,
    stdScreen: isStdScrn ? {
      width:    stdScreenW,
      height:   stdScreenH,
      qty:      stdScreenQty,
      label,
    } : null,

    // ── Summary (footer bars on cut list card + glazing page) ─────────────────
    // Frame Size: C20=indivH, B20=indivW
    // Sash Size:  sashWRef=C49, sashHRef=D49
    // Sash Weld:  IF(White, +0.75, ±0) on both dims
    // C Sash Size (Q/H/Q): W=overallW/2-6, H=height-3
    summary: {
      sashWidth:    sashWRef,          // C49
      sashHeight:   sashHRef,          // D49
      glassW,
      glassH,
      mullCenter,
      notch,
      sashWeldW,
      sashWeldH,
      ...(isQHQ ? {
        sashWeldW_C: centerSashW_ref,  // overallW/2-6
        sashWeldH_C: centerSashH_ref,  // height-3
      } : {}),
    },
  };
}